import PageAnimator from "../../components/ui/PageAnimator";
import { useEffect, useMemo, useState } from "react";
import api from "../../api";
import { motion } from "framer-motion";
import { Users, Briefcase } from "lucide-react";
import { Button } from "../../components/ui/button";
import PaginationFooter from "../../components/ui/PaginationFooter";
import { getPaginationState } from "../../utils/pagination";
import PartenaireCard from "../../features/partenaires/components/PartenaireCard";
import PartenaireDetail from "../../features/partenaires/components/PartenaireDetail";
import PartenaireForm from "../../features/partenaires/components/PartenaireForm";
import SearchBar from "../../features/partenaires/components/SearchBar";

const PAGE_SIZE = 12;
const ALL_CLASSES = "Toutes les classes";

function readPage(data, isArchivesView, requestedPage) {
  const items = data?.[isArchivesView ? "archives" : "partenaires"];
  const totalItems = data?.totalItems;
  const totalPages = data?.totalPages;
  const currentPage = data?.currentPage;
  const expectedPages = Math.ceil(totalItems / PAGE_SIZE);

  if (
    !Array.isArray(items) ||
    !items.every((item) => item && typeof item === "object" && item.id != null) ||
    !Number.isInteger(totalItems) || totalItems < 0 ||
    !Number.isInteger(totalPages) || totalPages < 0 ||
    (totalPages !== expectedPages && !(totalItems === 0 && totalPages === 1)) ||
    currentPage !== requestedPage ||
    items.length > PAGE_SIZE || items.length > totalItems
  ) {
    throw new Error("Réponse de pagination des partenaires invalide.");
  }

  return { items, totalItems };
}

export default function Partenaires() {
  // Cette requête est l'unique source des rechargements et de la vue demandée.
  const [request, setRequest] = useState({ page: 1, isArchivesView: false, revision: 0 });
  const [result, setResult] = useState({ key: null, status: "loading" });
  const [query, setQuery] = useState("");
  const [selectedClasse, setSelectedClasse] = useState(ALL_CLASSES);
  const [partenaireSelectionne, setPartenaireSelectionne] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingPartenaire, setEditingPartenaire] = useState(null);
  const { page, isArchivesView, revision } = request;
  const requestKey = `${isArchivesView}-${page}-${revision}`;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setResult({ key: requestKey, status: "loading" });

    async function loadPage() {
      try {
        const response = await api.get(isArchivesView ? "/partenaires/archives" : "/partenaires", {
          params: { page, limit: PAGE_SIZE },
          signal: controller.signal,
        });
        if (cancelled) return;

        const nextResult = readPage(response.data, isArchivesView, page);
        const { safePage } = getPaginationState({ page, pageSize: PAGE_SIZE, totalItems: nextResult.totalItems });
        if (safePage !== page) {
          // Une mutation peut avoir supprimé la dernière page : charger ses vraies cartes.
          setRequest((previous) => ({ ...previous, page: safePage }));
          return;
        }

        const expectedItems = Math.min(PAGE_SIZE, Math.max(0, nextResult.totalItems - (page - 1) * PAGE_SIZE));
        if (nextResult.items.length !== expectedItems) {
          throw new Error("Le nombre de partenaires ne correspond pas au total annoncé.");
        }
        setResult({ key: requestKey, status: "success", ...nextResult });
      } catch (error) {
        if (cancelled) return;
        setResult({ key: requestKey, status: "error" });
      }
    }

    loadPage();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [page, isArchivesView, requestKey]);

  const loading = result.key !== requestKey || result.status === "loading";
  const hasError = !loading && result.status === "error";
  const ready = !loading && !hasError;
  const filteredPartenaires = useMemo(() => {
    if (result.status !== "success") return [];
    const normalizedQuery = query.trim().toLocaleLowerCase("fr");
    return result.items.filter((partenaire) => {
      const matchesQuery = !normalizedQuery || Object.values(partenaire).some((value) =>
        (typeof value === "string" || typeof value === "number") &&
        String(value).toLocaleLowerCase("fr").includes(normalizedQuery)
      );
      const matchesClasse = !selectedClasse || selectedClasse === ALL_CLASSES ||
        partenaire.classe_actifs === selectedClasse;
      return matchesQuery && matchesClasse;
    });
  }, [result, query, selectedClasse]);
  const hasLocalFilter = Boolean(query.trim()) || Boolean(selectedClasse && selectedClasse !== ALL_CLASSES);

  const refreshData = () => {
    setRequest((previous) => ({ ...previous, revision: previous.revision + 1 }));
  };

  const handleSavePartenaire = async (data) => {
    try {
      if (!data.id) {
        await api.post("/partenaires", data, { headers: { "Content-Type": "application/json" } });
      } else {
        await api.put(`/partenaires/${data.id}`, data, { headers: { "Content-Type": "application/json" } });
      }
      refreshData();
      setFormOpen(false);
      setEditingPartenaire(null);
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
    }
  };

  return (
    <div className="p-4 sm:p-8 z-10 h-full w-full flex flex-col">
      <PageAnimator>
        <div
          className="relative z-20 flex shrink-0 flex-col gap-6 overflow-visible workspace-card bg-cover bg-center bg-no-repeat p-4 sm:p-6 mb-6 xl:flex-row xl:items-center xl:justify-between"
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
          />
          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
              <Users className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-inverse">
                {isArchivesView ? "Partenaires Archivés" : "Réseau de Partenaires"}
              </h1>
              <p className="mt-1 text-sm text-inverse opacity-80">
                Gérez vos partenaires, leurs profils et leur conformité.
              </p>
            </div>
          </div>
          <div className="relative z-10 flex flex-col sm:flex-row items-center w-full xl:w-auto min-w-0">
            <SearchBar
              query={query}
              onQueryChange={setQuery}
              selectedClasse={selectedClasse}
              onClasseChange={(value) => {
                setSelectedClasse(value);
                setQuery("");
              }}
              onAdd={() => {
                setEditingPartenaire(null);
                setFormOpen(true);
              }}
              onArchives={() => {
                setRequest((previous) => ({ ...previous, page: 1, isArchivesView: !previous.isArchivesView }));
                setPartenaireSelectionne(null);
              }}
              isArchivesView={isArchivesView}
            />
          </div>
        </div>

        <section aria-label={isArchivesView ? "Liste des partenaires archivés" : "Liste des partenaires"} aria-busy={loading}>
          {loading && (
            <div role="status" className="workspace-card p-12 text-center text-muted">
              Chargement des partenaires…
            </div>
          )}
          {hasError && (
            <div role="alert" className="workspace-card p-8 text-center">
              <p className="text-muted">Impossible de charger les partenaires.</p>
              <Button type="button" variant="outline" className="mt-4" onClick={refreshData}>
                Réessayer
              </Button>
            </div>
          )}
          {ready && (
            <motion.div
              key={requestKey}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              {filteredPartenaires.length === 0 ? (
                <div className="col-span-full flex flex-col items-center justify-center p-16 workspace-card border-dashed border-2 bg-surface/50 mt-4">
                  <Briefcase className="h-16 w-16 text-subtle mb-4" aria-hidden="true" />
                  <h3 className="text-xl font-bold text-primary">
                    {hasLocalFilter ? "Aucun résultat sur cette page" : isArchivesView ? "Aucune archive" : "Aucun partenaire trouvé"}
                  </h3>
                  <p className="text-sm text-subtle text-center mt-2 max-w-md">
                    {hasLocalFilter
                      ? "Modifiez vos critères ou consultez une autre page."
                      : isArchivesView
                        ? "Aucun partenaire n'est archivé actuellement."
                        : "Vous n'avez pas encore de partenaires. Cliquez sur 'Ajouter' pour commencer."}
                  </p>
                </div>
              ) : filteredPartenaires.map((partenaire) => (
                <PartenaireCard
                  key={partenaire.id}
                  partenaire={partenaire}
                  onClick={() => setPartenaireSelectionne(partenaire)}
                  onEdit={() => {
                    setEditingPartenaire(partenaire);
                    setFormOpen(true);
                  }}
                  refreshData={refreshData}
                />
              ))}
            </motion.div>
          )}
        </section>

        {ready && result.totalItems > 0 && (
          <div className="mt-8">
            <PaginationFooter
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={result.totalItems}
              onPageChange={(nextPage) => setRequest((previous) => ({ ...previous, page: nextPage }))}
              ariaLabel={isArchivesView ? "Pagination des partenaires archivés" : "Pagination des partenaires"}
              summaryText={hasLocalFilter
                ? `${filteredPartenaires.length} résultat(s) sur cette page ; ${result.totalItems} résultat(s) au total`
                : undefined}
            />
          </div>
        )}

        {partenaireSelectionne && (
          <PartenaireDetail
            partenaire={partenaireSelectionne}
            onClose={() => setPartenaireSelectionne(null)}
            onEdit={(partenaire) => {
              setEditingPartenaire(partenaire);
              setFormOpen(true);
            }}
            refreshData={refreshData}
          />
        )}
        {formOpen && (
          <PartenaireForm
            open={formOpen}
            partenaire={editingPartenaire}
            onClose={() => setFormOpen(false)}
            onSave={handleSavePartenaire}
          />
        )}
      </PageAnimator>
    </div>
  );
}
